// src/queries/event.query.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getRequest, postRequest, deleteRequest, putRequest } from 'utils/http'

const EVENT_QUERY_KEY = 'events'

// =========================================================
//                  EVENT QUERIES
// =========================================================

/**
 * Fetches all events with pagination.
 * Backend: GET /api/events (calls EventController@index or getData)
 * @param {number} current - Current page number
 * @param {number} pageSize - Number of items per page
 */
export const getEvents = async (current, pageSize) => {
  return getRequest('events', {
    params: {
      page: current,
      pageSize: pageSize, // Your backend repository.all() doesn't explicitly paginate,
      // but this param will be useful if you add pagination to your repository later.
      // For now, it will likely return all events.
    },
  })
}

/**
 * Hook to fetch events with pagination.
 */
export const useGetEvents = (current, pageSize) => {
  return useQuery({
    queryKey: [EVENT_QUERY_KEY, current, pageSize],
    queryFn: () => getEvents(current, pageSize),
  })
}

/**
 * Creates a new event.
 * Backend: POST /api/events (calls EventController@store)
 * Requires 'multipart/form-data' for image uploads.
 * @param {FormData} newEventData - FormData object containing event details and optional cover_image.
 */
export const createEvent = async newEventData => {
  return postRequest('events', newEventData)
}

/**
 * Hook to create a new event.
 * On success, it invalidates the 'events' query to trigger a refetch of the list.
 */
export const useCreateEvent = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEY] })
    },
    onError: error => {
      console.error('Failed to create event:', error)
      // Implement UI feedback here (e.g., toast.error)
    },
  })
}

/**
 * Updates an existing event.
 * Backend: POST /api/events/{id} (with _method=PUT) (calls EventController@update)
 * Requires 'multipart/form-data' for optional image uploads and method spoofing.
 * @param {FormData} updatedEventData - FormData object containing event details, ID, and optional new cover_image.
 */
export const updateEvent = async updatedEventData => {
  // The URL still needs the ID
  return postRequest(`events/${updatedEventData.get('id')}`, updatedEventData)
}

/**
 * Hook to update an existing event.
 * On success, it invalidates the 'events' query to trigger a refetch of the list.
 */
export const useUpdateEvent = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateEvent,
    onSuccess: (_, updatedEventData) => {
      queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEY] })
      const eventId = updatedEventData.get('id')
      if (eventId) {
        queryClient.invalidateQueries({ queryKey: ['event-ticket-preview', eventId] })
      }
    },
    onError: error => {
      console.error('Failed to update event:', error)
      // Implement UI feedback here (e.g., toast.error)
    },
  })
}

/**
 * Deletes an event.
 * Backend: DELETE /api/events/{id} (calls EventController@destroy)
 * @param {string} id - The ID of the event to delete.
 */
export const deleteEvent = async id => {
  return deleteRequest(`events/${id}`)
}

/**
 * Hook to delete an event.
 * On success, it invalidates the 'events' query to reflect the change.
 */
export const useDeleteEvent = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEY] })
    },
    onError: error => {
      console.error('Failed to delete event:', error)
      // Implement UI feedback here (e.g., toast.error)
    },
  })
}

export const getEvent = async (id) => {
  return getRequest(`events/${id}`)
}

export const useGetEvent = (id) => {
  return useQuery({
    queryKey: [EVENT_QUERY_KEY, id],
    queryFn: () => getEvent(id),
    enabled: !!id,
  })
}

export const syncEventTiers = async ({ eventId, tiers }) => {
  return putRequest(`events/${eventId}/ticket-tiers/sync`, { tiers })
}

export const useSyncEventTiers = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: syncEventTiers,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEY, variables.eventId] })
    },
  })
}

export const getTicketPreview = async (eventId) => {
  return getRequest(`events/${eventId}/ticket-preview`)
}

export const useGetTicketPreview = (eventId, options = {}) => {
  return useQuery({
    queryKey: ['event-ticket-preview', eventId],
    queryFn: () => getTicketPreview(eventId),
    enabled: !!eventId,
    ...options,
  })
}

